import { ChangeEvent, JSX } from 'react';
import { MenuItem, Stack, TextField, Typography } from '@mui/material';

export interface AdminMiniSchedule {
    scheduleType: string;
    scheduledAt: string;
    startDate: string;
    endDate: string;
}

export interface AdminMiniSchedulePanelProps {
    value: AdminMiniSchedule;
    onChange: (value: AdminMiniSchedule) => void;
    runNoun: string;
    disabled?: boolean;
}

/**
 * When a run goes out.
 *
 * **The ids carry two underscores, and that is load-bearing.** HQAPI-123: HQ used to offer
 * `next_3` with one while every reader in `hq_api` matched on `next__`, so the prefix never
 * matched, `intval('next_3')` came out as 0, and every rolling run was spread across a zero-month
 * window - which is to say all of its targets went at once. The feature had never staggered a
 * single send. `RunSchedule` reads both spellings now, for runs already queued under the old one.
 */
export const SCHEDULE_OPTIONS: { id: string, name: string, detail: string }[] = [
    { id: 'now', name: 'Immediately', detail: 'Sends while you wait.' },
    { id: 'date', name: 'On a date', detail: 'Everybody receives it at the same moment.' },
    { id: 'next__3', name: 'Across three months', detail: 'A random weekday each, spread over the quarter.' },
    { id: 'next__6', name: 'Across six months', detail: 'A random weekday each, spread over six months.' },
    { id: 'next__12', name: 'Across a year', detail: 'A random weekday each, spread over the year.' },
];

function isRolling(scheduleType: string): boolean {
    return scheduleType.startsWith('next__');
}

export const AdminMiniSchedulePanel: (props: AdminMiniSchedulePanelProps) => JSX.Element =
    (props: AdminMiniSchedulePanelProps): JSX.Element => {
        const { value, onChange } = props;
        const chosen = SCHEDULE_OPTIONS.find(
            (option: { id: string }): boolean => option.id === value.scheduleType
        );

        return (
            <Stack spacing={ 2 } sx={ { width: '100%' } }>
                <Typography sx={ {
                    fontSize: 'var(--wf-eyebrow)',
                    letterSpacing: 'var(--wf-track)',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontWeight: 700,
                } }>
                    { 'Goes out' }
                </Typography>

                <TextField select
                           size="small"
                           label="When"
                           value={ value.scheduleType }
                           disabled={ props.disabled }
                           helperText={ chosen?.detail }
                           onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                               onChange({ ...value, scheduleType: e.target.value }) }>
                    { SCHEDULE_OPTIONS.map((option): JSX.Element => (
                        <MenuItem key={ option.id } value={ option.id }>{ option.name }</MenuItem>
                    )) }
                </TextField>

                { value.scheduleType === 'date' && (
                    <TextField type="datetime-local"
                               size="small"
                               label="Date and time"
                               value={ value.scheduledAt }
                               disabled={ props.disabled }
                               slotProps={ { inputLabel: { shrink: true } } }
                               onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                   onChange({ ...value, scheduledAt: e.target.value }) } />
                ) }

                { isRolling(value.scheduleType) && (
                    <TextField type="date"
                               size="small"
                               label="Start from"
                               value={ value.startDate }
                               disabled={ props.disabled }
                               slotProps={ { inputLabel: { shrink: true } } }
                               helperText="Leave empty to start now"
                               onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                   onChange({ ...value, startDate: e.target.value }) } />
                ) }

                <TextField type="date"
                           size="small"
                           label="Stop listening after"
                           value={ value.endDate }
                           disabled={ props.disabled }
                           slotProps={ { inputLabel: { shrink: true } } }
                           helperText={ `When this ${ props.runNoun } stops waiting for replies` }
                           onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                               onChange({ ...value, endDate: e.target.value }) } />
            </Stack>
        );
    };
